// File: src/pages/legend/FAQ.jsx
import React, { useState } from "react";

const faqData = {
  Lawyer: [
    {
      sentiment: "negative",
      question: "I’m too busy with cases; will I have time to use this platform?",
      answer:
        "You don’t need to spend hours. The platform provides concise, actionable legal updates, case summaries, and AI-guided insights to save time and enhance practice efficiency.",
    },
    {
      sentiment: "negative",
      question: "Why should I rely on this platform over traditional legal research?",
      answer:
        "Unlike generic resources, LEGEND offers personalized case insights, CLE tracking, and AI-driven recommendations tailored to your practice area.",
    },
    {
      sentiment: "negative",
      question: "I’m not tech-savvy; can I still benefit?",
      answer:
        "Absolutely. LEGEND is visual, voice-enabled, and intuitive. Lawyers and students can navigate easily using icons, videos, and guided workflows.",
    },
    {
      sentiment: "neutral",
      question: "Can I track my CLE credits and case progress?",
      answer:
        "Yes. The dashboard shows CLE credit history, case milestones, court schedules, and upcoming deadlines in one place.",
    },
    {
      sentiment: "neutral",
      question: "Is there a mobile app available?",
      answer:
        "Yes. The app works on smartphones and tablets for on-the-go updates, while the web version provides deeper analytics.",
    },
    {
      sentiment: "positive",
      question: "Can this platform help me draft legal documents faster?",
      answer:
        "Yes. AI-assisted drafting provides templates, clause suggestions, and precedent references to save time and improve accuracy.",
    },
    {
      sentiment: "positive",
      question: "Will it improve my case research and insights?",
      answer:
        "Absolutely. AI filters cases by relevance, jurisdiction, and outcome, helping you prepare stronger arguments efficiently.",
    },
  ],

  LawFirm: [
    {
      sentiment: "negative",
      question: "Will adopting this platform disrupt existing workflows?",
      answer:
        "LEGEND integrates smoothly with your current tools and document management systems, enhancing productivity without major workflow changes.",
    },
    {
      sentiment: "negative",
      question: "How do we ensure data confidentiality?",
      answer:
        "All data is encrypted, access-controlled, and stored securely with multi-factor authentication and compliance with legal data standards.",
    },
    {
      sentiment: "neutral",
      question: "Can multiple lawyers in our firm collaborate on the platform?",
      answer:
        "Yes, you can assign roles, share case notes, and collaborate on drafting and research within the firm securely.",
    },
    {
      sentiment: "neutral",
      question: "Does the platform support reporting and analytics for the firm?",
      answer:
        "Yes, track workload, case progress, CLE compliance, and performance metrics across all lawyers in your firm.",
    },
    {
      sentiment: "positive",
      question: "Will it improve overall firm productivity?",
      answer:
        "AI automation, document templates, and integrated workflows allow lawyers to spend more time on high-value tasks, boosting firm efficiency.",
    },
    {
      sentiment: "positive",
      question: "Can we use it to attract new clients?",
      answer:
        "Yes. Verified profiles, digital identity, and showcases of expertise make it easier for clients to find and trust your firm.",
    },
  ],

  Regulator: [
    {
      sentiment: "negative",
      question: "How can we trust the data submitted by lawyers or firms?",
      answer:
        "The platform cross-verifies credentials, CLE records, and case outcomes to provide reliable and verified data for regulatory oversight.",
    },
    {
      sentiment: "negative",
      question: "Will this add extra compliance burden?",
      answer:
        "No. LEGEND automates reporting and compliance monitoring, reducing manual effort while ensuring accurate oversight.",
    },
    {
      sentiment: "neutral",
      question: "Can we generate reports for policy decisions?",
      answer:
        "Yes. Dashboards allow aggregation by jurisdiction, specialization, and CLE compliance for informed policy-making.",
    },
    {
      sentiment: "neutral",
      question: "Does it track legal education and training participation?",
      answer:
        "Yes. CLE modules, workshops, and webinars completed by lawyers are logged automatically for reporting.",
    },
    {
      sentiment: "positive",
      question: "Will it help improve legal standards and compliance?",
      answer:
        "Data-driven insights help identify gaps in training, adherence, and case outcomes, enabling regulators to strengthen the legal ecosystem.",
    },
  ],

  LawStudent: [
    {
      sentiment: "negative",
      question: "I’m overwhelmed with studies; will this platform help?",
      answer:
        "LEGEND provides bite-sized lessons, case summaries, and AI-guided study plans, making legal education manageable alongside your curriculum.",
    },
    {
      sentiment: "negative",
      question: "Is this just another learning portal?",
      answer:
        "No. It combines CLE, research, mentorship, and practical insights, bridging theory and real-world practice.",
    },
    {
      sentiment: "neutral",
      question: "Can I track my learning progress and CLE credits?",
      answer:
        "Yes. Dashboards show completed modules, upcoming workshops, and credit accumulation in real time.",
    },
    {
      sentiment: "positive",
      question: "Will this platform help me become practice-ready?",
      answer:
        "Yes. AI-assisted case studies, simulations, and mentorship modules prepare you for practical legal work and internships.",
    },
  ],

  LegalAdvisor: [
    {
      sentiment: "negative",
      question: "Will this replace my advisory role?",
      answer:
        "No. LEGEND enhances your advisory capability by automating routine research, so you can focus on strategy and client guidance.",
    },
    {
      sentiment: "negative",
      question: "How accurate is AI in legal recommendations?",
      answer:
        "AI provides probabilistic insights based on precedent, statutes, and expert-curated databases, but final judgment remains with the advisor.",
    },
    {
      sentiment: "neutral",
      question: "Can I provide recommendations and monitor client adoption?",
      answer:
        "Yes, you can submit advice, track implementation, and monitor impact through dashboards.",
    },
    {
      sentiment: "positive",
      question: "Will it make my advisory more impactful?",
      answer:
        "By highlighting key issues, legal risks, and case precedents, AI makes your guidance faster, more accurate, and actionable.",
    },
  ],

  Auditor: [
    {
      sentiment: "negative",
      question: "Can we trust the authenticity of law firm records and CLE submissions?",
      answer:
        "LEGEND cross-verifies submissions, CLE credits, and certifications with authoritative databases to ensure authenticity.",
    },
    {
      sentiment: "negative",
      question: "Will the platform comply with audit and regulatory standards?",
      answer:
        "Yes. It meets legal, data privacy, and compliance standards, ensuring all audits are accurate and accepted.",
    },
    {
      sentiment: "neutral",
      question: "Can we export reports for audits or oversight purposes?",
      answer:
        "Yes. Data can be exported in Excel, PDF, or CSV formats for auditing or regulatory review.",
    },
    {
      sentiment: "positive",
      question: "Will it reduce manual auditing efforts?",
      answer:
        "Automated tracking, AI anomaly detection, and compliance dashboards significantly reduce the time and effort required for audits.",
    },
  ],
};

// Color coding for sentiment
const sentimentColors = {
  negative: "text-orange-400",
  neutral: "text-gray-400",
  positive: "text-green-400",
};

export default function LegendFAQ() {
  const stakeholders = Object.keys(faqData);
  const [selectedStakeholder, setSelectedStakeholder] = useState(
    stakeholders[0]
  );
  const [expandedIndex, setExpandedIndex] = useState(null);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-8">
      <h1 className="text-lg sm:text-xl font-bold mb-6 text-center">
        Think5Whys Legend FAQ
      </h1>

      {/* Stakeholder Tabs */}
      <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-6">
        {stakeholders.map((stakeholder) => (
            <button
            key={stakeholder}
            onClick={() => {
                setSelectedStakeholder(stakeholder);
                setExpandedIndex(null);
            }}
            className={`px-4 py-2 rounded ${
                selectedStakeholder === stakeholder
                ? "bg-yellow-600 text-gray-900"
                : "bg-gray-700 hover:bg-gray-600"
            } font-semibold text-sm sm:text-base`}
            >
            {stakeholder}
            </button>
        ))}
      </div>


      {/* FAQ List */}
      <div className="max-w-4xl mx-auto">
        {faqData[selectedStakeholder].map((faq, index) => (
          <div
            key={index}
            className="border-b border-gray-700 py-3 cursor-pointer"
            onClick={() =>
              setExpandedIndex(expandedIndex === index ? null : index)
            }
          >
            <div className="flex justify-between items-center">
              <h3
                className={`font-medium ${sentimentColors[faq.sentiment]}`}
              >
                {faq.question}
              </h3>
              <span className="text-gray-400 text-xl">
                {expandedIndex === index ? "-" : "+"}
              </span>
            </div>
            {expandedIndex === index && (
              <p className="mt-2 text-gray-300">{faq.answer}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
